import React from 'react'

interface JsonDisplayProps {
  data: string
}

export const JsonDisplay: React.FC<JsonDisplayProps> = ({ data }) => {
  const parsedData = React.useMemo(() => {
    try {
      return JSON.parse(data)
    } catch (e) {
      return {}
    }
  }, [data])

  if (
    !parsedData.properties ||
    Object.keys(parsedData.properties).length === 0
  ) {
    return <div className="text-sm text-gray-500">Not required</div>
  }

  const renderProperties = (properties: string | object) => {
    return Object.entries(properties).map(
      ([key, value]: [string, Record<string, string | string[]>]) => (
        <tr key={key} className="border-b border-gray-200">
          <td className="w-[120px] px-3 py-3 text-sm font-medium text-gray-900">
            {key}
          </td>
          <td className="max-w-[200px] px-3 py-3 text-sm text-gray-500">
            <div className="break-words">{value.description || '-'}</div>
          </td>
          <td className="w-[150px] px-3 py-3 text-sm text-gray-500">
            {value.type}
            {Array.isArray(value.enum) && (
              <div className="mt-1 break-words text-xs text-gray-400">
                Allowed: {value.enum.join(', ')}
              </div>
            )}
          </td>
          <td className="w-[150px] px-3 py-3 text-sm text-gray-500">
            {value.default !== undefined ? String(value.default) : '-'}
          </td>
          <td className="w-[100px] px-3 py-3 text-sm text-gray-500">
            {parsedData.required?.includes(key) ? (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                Required
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                Optional
              </span>
            )}
          </td>
        </tr>
      ),
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="overflow-y-auto">
        <table className="w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="sticky top-0 z-10 w-[120px] bg-gray-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Field
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 max-w-[200px] bg-gray-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Description
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 w-[150px] bg-gray-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Type
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 w-[150px] bg-gray-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Default
              </th>
              <th
                scope="col"
                className="sticky top-0 z-10 w-[100px] bg-gray-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Required
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {renderProperties(parsedData.properties)}
          </tbody>
        </table>
      </div>
    </div>
  )
}
